import { JSX, useContext } from 'react';
import { FieldPath, UseFormReturn } from 'react-hook-form';

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from 'components/Accordion';
import { LoadRefPillInput } from 'components/input/LoadRefPillInput';
import { Flex, Grid } from 'components/layout';
import { SidebarStateContext } from 'contexts/sidebarStateContext';
import { useFieldAttributes } from 'hooks/useLoadContext';
import { LoadDateTimeInput } from 'pages/LoadView/LoadInformation/Components';
import { LoadStopTypes, NormalizedLoad, TMSLocation } from 'types/Load';
import { getFieldAttribute } from 'types/LoadAttributes';
import { Maybe } from 'types/UtilityTypes';
import {
  LoadStopNestedSection,
  useOpenNestedStopDetailsOnSuggestion,
} from 'utils/expandStopDetailsOnSuggestion';
import { datetimeFieldOptions } from 'utils/formValidators';

import { LoadBuildingTextInput } from '../components/LoadBuildingTextInput';

export type StopFormProps = {
  stop: LoadStopTypes;
  formMethods: UseFormReturn<NormalizedLoad>;
  originalSuggestionData?: NormalizedLoad;
  isUpdateMode?: boolean;
  // Optional – TAI uses quick entry only; location props kept for type compatibility
  isLoadingLocations?: boolean;
  locations?: Maybe<TMSLocation[]>;
  handleRefreshLocations?: () => void;
  setLocations?: React.Dispatch<React.SetStateAction<Maybe<TMSLocation[]>>>;
};

export function StopForm({
  stop,
  formMethods,
  originalSuggestionData,
  isUpdateMode = false,
}: StopFormProps): JSX.Element {
  const { control, watch, setValue, getValues } = formMethods;
  const watchedStop = watch(stop);
  const fieldAttrs = useFieldAttributes();
  const refNumberAttr = getFieldAttribute(fieldAttrs, `${stop}.refNumber`);
  const {
    currentState: { clickedSuggestion },
  } = useContext(SidebarStateContext);
  const [additionalDetailsOpen, setAdditionalDetailsOpen] =
    useOpenNestedStopDetailsOnSuggestion(
      clickedSuggestion,
      stop,
      LoadStopNestedSection.AdditionalDetails
    );

  return (
    <>
      {/* Quick entry: all location fields as text inputs (no TMS location select) */}
      <LoadBuildingTextInput
        name={`${stop}.name` as FieldPath<NormalizedLoad>}
        label='Name'
        required={false}
        placeholder='Location name'
        readOnly={isUpdateMode}
      />

      <LoadBuildingTextInput
        name={`${stop}.addressLine1` as FieldPath<NormalizedLoad>}
        label='Address Line 1'
        placeholder='Street address'
        readOnly={isUpdateMode}
      />

      <LoadBuildingTextInput
        name={`${stop}.addressLine2` as FieldPath<NormalizedLoad>}
        label='Address Line 2'
        placeholder='Suite, unit, etc.'
        readOnly={isUpdateMode}
      />

      <Grid cols='4' gap='sm' width='full' className='mx-0'>
        <Flex className='col-span-3'>
          <LoadBuildingTextInput
            name={`${stop}.city` as FieldPath<NormalizedLoad>}
            label='City'
            required
            placeholder='City'
            readOnly={isUpdateMode}
          />
        </Flex>
        <Flex className='col-span-1'>
          <LoadBuildingTextInput
            name={`${stop}.state` as FieldPath<NormalizedLoad>}
            label='State'
            required
            placeholder='State'
            readOnly={isUpdateMode}
          />
        </Flex>
      </Grid>

      <Grid cols='2' gap='sm' width='full' className='mx-0'>
        <LoadBuildingTextInput
          name={`${stop}.zipCode` as FieldPath<NormalizedLoad>}
          label='Zip Code'
          required
          placeholder='Zip code'
          aiIconOnly={true}
          readOnly={isUpdateMode}
        />
        <LoadBuildingTextInput
          name={`${stop}.country` as FieldPath<NormalizedLoad>}
          label='Country'
          required
          placeholder='Country'
          readOnly={isUpdateMode}
        />
      </Grid>

      <Accordion
        type='single'
        className='w-full'
        collapsible
        value={additionalDetailsOpen}
        onValueChange={setAdditionalDetailsOpen}
      >
        <AccordionItem
          value={LoadStopNestedSection.AdditionalDetails}
          className='border-b-0'
        >
          <AccordionTrigger className='text-sm font-medium py-2'>
            Additional details
          </AccordionTrigger>
          <AccordionContent className='pt-2'>
            <Flex direction='col' gap='md'>
              <LoadBuildingTextInput
                name={`${stop}.apptNote` as FieldPath<NormalizedLoad>}
                label='Notes'
                placeholder='Enter location notes'
                readOnly={isUpdateMode}
              />

              <LoadBuildingTextInput
                name={`${stop}.businessHours` as FieldPath<NormalizedLoad>}
                label='Instructions'
                placeholder='Enter special instructions'
                readOnly={isUpdateMode}
              />

              {!refNumberAttr?.isNotSupported && (
                <LoadRefPillInput
                  name={`${stop}.refNumber` as FieldPath<NormalizedLoad>}
                  control={control}
                  label='Reference Number'
                  originalSuggestionData={originalSuggestionData || getValues()}
                  disabled={Boolean(isUpdateMode || refNumberAttr?.isReadOnly)}
                />
              )}

              <LoadBuildingTextInput
                name={`${stop}.locationType` as FieldPath<NormalizedLoad>}
                label='Location Type'
                placeholder='Enter location type'
                readOnly={isUpdateMode}
              />

              {stop === 'pickup' ? (
                <>
                  <LoadDateTimeInput
                    name={`${stop}.readyTime` as FieldPath<NormalizedLoad>}
                    label='Ready Time'
                    options={datetimeFieldOptions}
                    load={getValues()}
                  />
                  <LoadDateTimeInput
                    name={`${stop}.apptStartTime` as FieldPath<NormalizedLoad>}
                    label='Appointment Start Time'
                    options={datetimeFieldOptions}
                    load={getValues()}
                  />
                  <LoadDateTimeInput
                    name={`${stop}.apptEndTime` as FieldPath<NormalizedLoad>}
                    label='Appointment End Time'
                    options={datetimeFieldOptions}
                    load={getValues()}
                  />
                </>
              ) : (
                <>
                  <LoadDateTimeInput
                    name={`${stop}.mustDeliver` as FieldPath<NormalizedLoad>}
                    label='Must Deliver By'
                    options={datetimeFieldOptions}
                    load={getValues()}
                  />
                  <LoadDateTimeInput
                    name={`${stop}.apptStartTime` as FieldPath<NormalizedLoad>}
                    label='Appointment Start Time'
                    options={datetimeFieldOptions}
                    load={getValues()}
                  />
                  <LoadDateTimeInput
                    name={`${stop}.apptEndTime` as FieldPath<NormalizedLoad>}
                    label='Appointment End Time'
                    options={datetimeFieldOptions}
                    load={getValues()}
                  />
                </>
              )}

              <div className='mt-2'>
                <label className='flex items-center space-x-2'>
                  <input
                    type='checkbox'
                    checked={watchedStop?.tsaCompliant ?? false}
                    disabled={isUpdateMode}
                    onChange={(e) => {
                      setValue(
                        `${stop}.tsaCompliant` as FieldPath<NormalizedLoad>,
                        e.target.checked,
                        { shouldDirty: true }
                      );
                    }}
                    className='rounded border-neutral-400 text-info-600 focus:ring-info-500'
                  />
                  <span className='text-sm text-neutral-700'>
                    TSA Compliant
                  </span>
                </label>
              </div>
            </Flex>
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </>
  );
}
